import { useCallback, useRef } from "react"

import { Button } from "@atoms/index"

import Dialog from "@molecules/Dialog/Dialog"

import styles from "./InvoiceReverseCreditAllocationDialog.module.scss"
import { InvoiceReverseCreditAllocationDialogProps } from "./InvoiceReverseCreditAllocationDialog.types"

export default function InvoiceReverseCreditAllocationDialog(props: InvoiceReverseCreditAllocationDialogProps) {
    const { isOpen, onOpenChange, creditNoteNumber, amount, isHeldByQuickBooksDesktop, isReversing, onConfirm } = props

    // Store onOpenChange in a ref so the callback identity stays stable across the legacy card's re-renders.
    // The Popover's useEffect depends on onOpenChange, and an unstable reference causes an infinite re-render loop.
    const onOpenChangeRef = useRef(onOpenChange)
    onOpenChangeRef.current = onOpenChange

    const stableOnOpenChange = useCallback((isNowOpen: boolean) => {
        onOpenChangeRef.current(isNowOpen)
    }, [])

    // A successful reversal reloads the page; the button stays busy until then so a second click cannot fire again.
    const handleConfirm = () => {
        if (!isReversing) {
            onConfirm()
        }
    }

    return (
        <Dialog.Root mode="dialog" variant="default" isOpen={isOpen} onOpenChange={stableOnOpenChange}>
            <Dialog.Content>
                <Dialog.Header.Root>
                    <Dialog.Header.Title title="Reverse Credit Note" />
                    <Dialog.Header.CloseButton />
                </Dialog.Header.Root>
                <Dialog.Body>
                    <div className={styles.body}>
                        {isHeldByQuickBooksDesktop ? (
                            <>
                                <p className={styles.paragraph}>
                                    QuickBooks Desktop holds Credit Note {creditNoteNumber} ({amount}) against this
                                    invoice, so it can only be reversed here after it&apos;s removed there.
                                </p>
                                <p className={styles.paragraph}>
                                    Mark it for reversal, then remove it from the invoice in QuickBooks Desktop. The
                                    next QuickBooks sync reverses it here.
                                </p>
                            </>
                        ) : (
                            <p className={styles.paragraph}>
                                Reverse Credit Note {creditNoteNumber} ({amount}) on this invoice? The invoice will owe
                                that amount again.
                            </p>
                        )}
                    </div>
                </Dialog.Body>
                <Dialog.Footer.Root>
                    <Button variant="ghost" size="lg" type="button" onClick={() => stableOnOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        size="lg"
                        type="button"
                        onClick={handleConfirm}
                        isProcessing={isReversing}
                        isDisabled={isReversing}
                    >
                        {isHeldByQuickBooksDesktop ? "Mark for Reversal" : "Reverse"}
                    </Button>
                </Dialog.Footer.Root>
            </Dialog.Content>
        </Dialog.Root>
    )
}
